import { forwardRef, Fragment, RefCallback, useState } from 'react';
import { BiCheck } from '@react-icons/all-files/bi/BiCheck';
import { HiSelector } from '@react-icons/all-files/hi/HiSelector';
import { Combobox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { CgSpinner } from '@react-icons/all-files/cg/CgSpinner';
import { isArray } from 'lodash';
import { InputDefaultClassName } from '../utils';

export interface SearchableDropdownItem {
  label: string;
  value: string;
}

export interface SearchableDropdownProps {
  /** options for dropdown */
  items: SearchableDropdownItem[];
  /** label for form input */
  label?: string;
  /** message displayed when there are no filteredItems */
  noResultsMessage?: string;
  /** allow for multiple selected options */
  multiple?: boolean;
  /** search query */
  search?: string;
  setSearch?: (search: string) => void;
  filteredItems?: SearchableDropdownItem[];
  displayValueFunction?: (
    selected: SearchableDropdownItem | SearchableDropdownItem[],
  ) => string;
  onChange?: (
    selected: SearchableDropdownItem | SearchableDropdownItem[],
  ) => Promise<void> | void;
  inputClassName?: string;
  value?: SearchableDropdownItem | SearchableDropdownItem[];
  resultsLoading?: boolean;
  dropdownStyles?: {
    maxHeight?: string;
    minWidth?: string;
    maxWidth?: string;
  };
  'data-test-id'?: string;
}

export const SearchableDropdown = forwardRef(
  (
    {
      noResultsMessage = 'No results found.',
      ...props
    }: SearchableDropdownProps,
    ref,
  ) => {
    const [_search, _setSearch] = useState('');
    const [selected, setSelected] = useState<
      SearchableDropdownItem | SearchableDropdownItem[] | null
    >(props.value ?? null);
    const search = props?.search ?? _search;
    const setSearch = props?.setSearch ?? _setSearch;
    // this doesn't account for multiple selected items
    const filteredItems =
      props.filteredItems ?? search === ''
        ? props.items
        : props.items.filter((item) =>
            item.label.toLowerCase().includes(search.toLowerCase()),
          );

    const defaultDisplayValueFunction = (
      item: SearchableDropdownItem | SearchableDropdownItem[],
    ): string => {
      if (props.value) {
        const values = props.value;
        if (isArray<SearchableDropdownItem>(values)) {
          return '';
        }
        return props.items.find((i) => values.value === i.value)?.label ?? '';
      }
      if (item) {
        if (isArray(item)) {
          return item.map((i) => i.label).join(', ');
        }
        return item.label;
      }
      return '';
    };

    const displayValueFunction =
      props.displayValueFunction ?? defaultDisplayValueFunction;

    const handleOnChange = (
      value: SearchableDropdownItem | SearchableDropdownItem[],
    ) => {
      props.onChange?.(value);
      setSelected(value);
    };

    return (
      <div>
        {props.multiple && isArray(selected) && (
          <div className="mb-4">
            <p className="block text-sm font-medium transition-all whitespace-nowrap">
              Selected
            </p>
            <div className="bg-gray-50 rounded-md border w-full overflow-scroll p-1 h-28">
              {selected.map((value, i) => (
                <div
                  className="bg-blue px-2 py-1 border-blue-dark text-white max-w-min rounded-md whitespace-nowrap m-1 inline-block float-left w-full"
                  key={value.value + i}
                >
                  <div className="flex w-full justify-between items-center text-sm">
                    <p className="w-5/6 truncate">{value.label}</p>
                    <div className="w-6">
                      <button
                        type="button"
                        className="ml-2 bg-white text-blue rounded-full w-3.5 h-3.5 flex items-center justify-center"
                        onClick={() => {
                          const newValues = isArray(selected)
                            ? selected?.filter(
                                (values) => values.value !== value.value,
                              )
                            : selected;

                          handleOnChange(newValues);
                        }}
                      >
                        x
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <Combobox
          as="div"
          value={selected}
          onChange={handleOnChange}
          multiple={props.multiple as any}
          ref={ref as any}
        >
          {({ open }) => (
            <>
              {props?.label && (
                <Combobox.Label className="block text-sm font-medium text-gray-700">
                  {props.label}
                </Combobox.Label>
              )}
              <div className="relative mt-1">
                <div
                  className={classNames(
                    'group rounded-md border border-gray-300 bg-white focus:outline-none sm:text-sm shadow-sm focus:ring-blue focus:border-blue-light transition-colors',
                    // InputDefaultClassName,
                    props.inputClassName,
                  )}
                >
                  <Combobox.Button as="div">
                    <Combobox.Input
                      className={classNames(
                        'w-full pr-[8%] border-none rounded-md truncate text-sm z-10',
                        InputDefaultClassName,
                        // props.inputClassName,
                      )}
                      onChange={(event) => setSearch(event.target.value)}
                      displayValue={displayValueFunction}
                      type="search" // to make 1pass/other extensions ignore
                      placeholder="Search..."
                      data-test-id={props['data-test-id']}
                    />
                  </Combobox.Button>
                  <Combobox.Button
                    className="absolute right-0 top-0 h-full flex items-center rounded-r-md focus:outline-none group-hover:bg-gray-200 transition-colors w-8 z-0 box-border"
                    onClick={(e: any) => e.stopPropagation()}
                    data-test-id="dropdown-button"
                  >
                    <HiSelector
                      className="h-5 w-5 text-gray-400 flex-1"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                </div>
                {(props.items.length > 0 ||
                  props.resultsLoading ||
                  search.length > 0) && (
                  <Transition
                    show={open}
                    as={Fragment}
                    enter="transition ease-out duration-60"
                    enterFrom="transform opacity-25 scale-98"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-60"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-98"
                  >
                    <Combobox.Options
                      className={classNames(
                        'absolute z-30 mt-1  w-full min-w-min overflow-auto rounded-md bg-white py-1 text-base shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm transition-color right-0',
                      )}
                      style={{
                        maxHeight: props.dropdownStyles?.maxHeight ?? '15rem',
                        minWidth:
                          props.dropdownStyles?.minWidth ?? 'min-content',
                      }}
                      data-test-id="dropdown-menu"
                    >
                      {props.resultsLoading ? (
                        <div className="py-6 bg-white flex items-center justify-center flex-col">
                          <CgSpinner
                            className={classNames('animate-spin text-gray-500')}
                            size={36}
                          />
                          <p className="text-gray-400 text-sm mt-3">
                            Loading suggestions...
                          </p>
                        </div>
                      ) : filteredItems.length ? (
                        filteredItems.map((item, i) => (
                          <>
                            <Combobox.Option
                              key={item.value + item.label + i}
                              value={item}
                              className={({ active, selected: sel }) =>
                                classNames(
                                  'relative cursor-pointer select-none py-2.5 pl-3 pr-9 transition-colors',
                                  active
                                    ? 'bg-blue text-white'
                                    : 'text-gray-800',
                                  sel && 'bg-gray-200 hover:bg-gray-300 ',
                                )
                              }
                              data-test-id="dropdown-menu-option"
                            >
                              {({ active, selected: sel }) => (
                                <>
                                  <span
                                    className={classNames(
                                      'block truncate',
                                      active && sel && 'text-white',
                                      sel && 'font-medium text-blue-dark',
                                    )}
                                  >
                                    {item.label}
                                  </span>

                                  {sel && (
                                    <span
                                      className={classNames(
                                        'absolute inset-y-0 right-0 flex items-center pr-4',
                                        active && 'text-white',
                                        sel && 'text-blue-dark',
                                      )}
                                    >
                                      <BiCheck
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                            {i !== filteredItems.length - 1 && (
                              <hr className="border-gray-300" />
                            )}
                          </>
                        ))
                      ) : (
                        <div className="w-full py-2 flex justify-center items-center">
                          <p className="text-gray-500 font-medium">
                            {noResultsMessage}
                          </p>
                        </div>
                      )}
                      <div className="h-0.5 w-full shadow-sm" />
                    </Combobox.Options>
                  </Transition>
                )}
              </div>
            </>
          )}
        </Combobox>
      </div>
    );
  },
);
